import { useChatStore } from '@/store'
import request from '@/utils/request'

// --- 参数与响应接口定义 ---

export interface ChatParams {
  sessionId?: string
  agentId?: number
  content: string
}

export interface ChatRes {
  // 根据实际后端返回的数据结构定义
  code?: number
  data: ChatParams
  message?: string
}

// 参数接口
export interface RunStreamParams {
  /*会话 */
  session?: {
    /*会话ID */
    id?: string
  }

  /*输入消息 */
  input: {
    /*角色 */
    role?: string

    /*消息部件 */
    parts?: {
      /*内容 */
      content?: string

      /*元数据 */
      metadata?: Record<string, unknown>

      /*部件名称 */
      name?: string

      /*内容类型 */
      content_type?: string

      /*内容URL（用于图片等二进制内容） */
      content_url?: string

      /*内容编码 */
      content_encoding?: string
    }[]
  }[]

  /*运行模式 */
  mode?: string

  /*智能体名称 */
  // agent_name: string
}

// 响应接口
export interface RunStreamRes {
  [key: string]: unknown
}

// 响应接口
export interface CancelRunRes {
  code?: number
  data: ChatParams
  message?: string
}

export interface LatestRunOutputPart {
  name: string | null
  content_type: string
  content: string
  content_encoding: string | null
  content_url: string | null
  metadata: unknown
}

export interface LatestRunOutputItem {
  role: string
  parts: LatestRunOutputPart[]
  created_at: string
  completed_at: string | null
}

export interface LatestRunInfo {
  run_id: string
  agent_name: string
  session_id: string
  status: string
  await_request: unknown
  output: LatestRunOutputItem[]
  error: unknown
  created_at: string
  finished_at: string | null
}

// 响应接口
export interface GetLatestRunStatusRes {
  code?: number
  msg?: string
  data: LatestRunInfo | null
}

// 响应接口
export interface ResumeStreamRes {
  [key: string]: unknown
}

// 会话列表单项
export interface SessionListItem {
  id: number
  sessionId: string
  agentId: number
  title: string
  status: number
  topFlag: boolean
  favorited: boolean
  shareCode: string
  lastActiveTime: string
  createTime: string
  defaultSession: boolean
  icon: string
  source: number
  modelId: string
}

export interface GetSessionListRes {
  code?: number
  msg?: string
  data: SessionListItem[]
}

/** 会话历史单条消息（v2 接口） */
export interface ChatHistoryMessage {
  role: string
  parts: {
    name: string | null
    content_type: string
    content: string
    content_encoding: string | null
    content_url: string | null
    metadata: unknown
  }[]
  created_at: string
  completed_at: string
}

export interface GetChatHistoryRes {
  code?: number
  msg?: string
  data: ChatHistoryMessage[]
  message?: string
}

export interface GetAgentListRes {
  code?: number
  data: {
    id: number
    name: string
    description: string
    avatar: string
    type: number
    sort: number
  }[]
  message?: string
}

export interface GetDefaultAgentRes {
  code?: number
  data: {
    id: number
    name: string
    description: string
    avatar: string
    type: number
    sort: number
  }
  message?: string
}

export interface DeleteSessionRes {
  code?: number
  data: string
  message?: string
}

/** 模型列表单项 */
export interface ModelListItem {
  name: string
  modelId: string
  capabilities: string[]
  modelIcon?: string
}

export interface GetModelListRes {
  code?: number
  msg?: string
  data: ModelListItem[]
}

/** 重命名会话参数 */
export interface RenameSessionParams {
  /** 会话ID */
  sessionId: string
  /** 新会话标题 */
  title: string
}

export interface RenameSessionRes {
  code?: number
  msg?: string
  data: string
}

/** 置顶会话响应 */
export interface PinSessionRes {
  code?: number
  msg?: string
  data: string
}

/** 收藏会话响应 */
export interface FavoriteSessionRes {
  code?: number
  msg?: string
  data: string
}

/** 举报会话参数 */
export interface ReportSessionParams {
  /** 会话ID */
  sessionId: string
  /** 举报类型：1-违法违规，2-色情低俗，3-虚假信息，4-侵权内容，5-其他 */
  reportType: number
  /** 举报补充说明（可选） */
  reason?: string
}

export interface ReportSessionRes {
  code?: number
  msg?: string
  data: string
}

function getSseHeaders(): Record<string, string> {
  const token = localStorage.getItem('token')
  let deviceId = localStorage.getItem('deviceId')

  if (!deviceId) {
    deviceId = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15)
    localStorage.setItem('deviceId', deviceId)
  }

  const isDesktop = window.matchMedia('(min-width: 1024px)').matches
  const isTablet = window.matchMedia('(min-width: 768px)').matches
  const deviceType = isDesktop ? 'desktop' : isTablet ? 'tablet' : 'mobile'

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'text/event-stream',
    'Cache-Control': 'no-cache',
    Connection: 'keep-alive',
    deviceId: deviceId,
    deviceType: deviceType,
    appVersion: '1.0.0'
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  return headers
}

async function streamSse(
  url: string,
  init: {
    method: 'GET' | 'POST'
    body?: string
    signal?: AbortSignal
  },
  callbacks: {
    onMessage?: (text: string) => void
    onError?: (error: unknown) => void
    onDone?: () => void
  }
): Promise<void> {
  let hasReceivedDone = false

  try {
    const response = await fetch(url, {
      method: init.method,
      headers: getSseHeaders(),
      ...(init.body ? { body: init.body } : {}),
      signal: init.signal
    })

    if (!response.ok) {
      if (response.status === 401) {
        window.dispatchEvent(new Event('auth:unauthorized'))
        const { isLoginModalOpen, setIsLoginModalOpen } = useChatStore.getState()
        if (!isLoginModalOpen) {
          setIsLoginModalOpen(true)
        }
        callbacks.onError?.(new Error('Unauthorized'))
        return
      }
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    if (!response.body) {
      throw new Error('ReadableStream is not supported by the browser.')
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder('utf-8')
    let buffer = ''
    let sseDataLines: string[] = []

    const emitSseMessage = () => {
      if (sseDataLines.length === 0) return false

      const dataStr = sseDataLines.join('\n').trim()
      sseDataLines = []
      if (!dataStr) return false

      if (dataStr === '[DONE]') {
        hasReceivedDone = true
        callbacks.onDone?.()
        return true
      }

      callbacks.onMessage?.(dataStr)
      return false
    }

    const processSseLines = (lines: string[]) => {
      for (const rawLine of lines) {
        const line = rawLine.trimEnd()
        const lineStartTrimmed = line.trimStart()

        if (lineStartTrimmed === '') {
          if (emitSseMessage()) {
            return true
          }
          continue
        }

        if (lineStartTrimmed.startsWith('data:')) {
          sseDataLines.push(lineStartTrimmed.slice(5).trimStart())
        }
      }
      return false
    }

    while (true) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split(/\r\n|\n|\r/)
      buffer = lines.pop() || ''
      if (processSseLines(lines)) {
        return
      }
    }

    buffer += decoder.decode()
    const tailLines = buffer.split(/\r\n|\n|\r/)
    buffer = tailLines.pop() || ''
    if (processSseLines(tailLines)) {
      return
    }

    if (buffer.trim() !== '') {
      const line = buffer.trimEnd().trimStart()
      if (line.startsWith('data:')) {
        sseDataLines.push(line.slice(5).trimStart())
      }
    }
    if (emitSseMessage()) {
      return
    }

    // 流在未收到 [DONE] 的情况下结束，视为异常断流（常见于网关/HTTP2超时）
    if (!hasReceivedDone) {
      throw new Error('SSE stream closed before [DONE]')
    }

    callbacks.onDone?.()
    return
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      callbacks.onDone?.()
      return
    }

    callbacks.onError?.(error)
    return
  }
}

// --- 接口实现 ---

/**
 * 发起聊天（非流式）
 * @param {ChatParams} params
 */
export function chat(params: ChatParams): Promise<ChatRes> {
  return request.post(`/ai-api/v1/agent/chat`, params)
}

/**
 * 发起ACP流式运行（SSE）
 * @param {RunStreamParams} params ACP协议运行请求
 * @param {object} callbacks 回调函数对象
 * @param {AbortSignal} [signal] 可选的取消信号
 */
export async function runStream(
  params: RunStreamParams,
  callbacks: {
    onMessage?: (text: string) => void
    onError?: (error: unknown) => void
    onDone?: () => void
  },
  signal?: AbortSignal
): Promise<void> {
  return streamSse(
    '/ai-api/v2/agent/chat/stream',
    {
      method: 'POST',
      body: JSON.stringify(params),
      signal
    },
    callbacks
  )
}

/**
 * 取消ACP运行
 * @param {string} runId 运行ID
 */
export function cancelRun(runId: string): Promise<CancelRunRes> {
  return request.post(`/ai-api/v2/agent/runs/${runId}/cancel`)
}

/**
 * 基于sessionId获取ACP运行状态
 * @param {string} sessionId 会话ID
 */
export function getLatestRunStatus(sessionId: string): Promise<GetLatestRunStatusRes> {
  return request.get(`/ai-api/v2/agent/sessions/${sessionId}/latestRun`)
}

/**
 * 恢复运行的流式输出（SSE）
 * @param {string} runId 运行ID
 * @param callbacks 回调函数对象
 * @param signal 可选的取消信号
 */
export async function resumeStream(
  runId: string,
  callbacks: {
    onMessage?: (text: string) => void
    onError?: (error: unknown) => void
    onDone?: () => void
  },
  signal?: AbortSignal
): Promise<void> {
  return streamSse(
    `/ai-api/v2/agent/runs/${runId}/stream`,
    {
      method: 'GET',
      signal
    },
    callbacks
  )
}

/**
 * 获取会话列表
 * @param agentId 智能体ID，不传则查询所有智能体的会话
 */
export function getSessionList(agentId?: number): Promise<GetSessionListRes> {
  const url = agentId !== undefined ? `/ai-api/v1/agent/session/list?agentId=${agentId}` : `/ai-api/v1/agent/session/list`
  return request.get(url)
}

/**
 * 获取会话聊天历史记录
 * @param {string} sessionId 会话ID
 */
export function getChatHistory(sessionId: string): Promise<GetChatHistoryRes> {
  return request.get(`/ai-api/v2/agent/session/history?sessionId=${sessionId}`)
}

/**
 * 获取模型列表
 */
export function getModelList(): Promise<GetModelListRes> {
  return request.get(`/ai-api/v2/agent/models`)
}

/**
 * 获取智能体列表（暂时不使用）
 */
export function getAgentList(): Promise<GetAgentListRes> {
  return request.get(`/ai-api/v1/agent/anon/list`)
}

/**
 * 获取默认智能体信息（暂时不使用）
 */
export function getDefaultAgent(): Promise<GetDefaultAgentRes> {
  return request.get(`/ai-api/v1/agent/anon/default`)
}

/**
 * 删除会话
 * @param {string} sessionId 会话ID
 */
export function deleteSession(sessionId: string): Promise<DeleteSessionRes> {
  return request.delete(`/ai-api/v1/agent/session?sessionId=${sessionId}`)
}

/**
 * 重命名会话
 * @param params.sessionId 会话ID
 * @param params.title 新会话标题
 */
export function renameSession(params: RenameSessionParams): Promise<RenameSessionRes> {
  return request.put(`/ai-api/v1/agent/session/rename`, params)
}

/**
 * 置顶/取消置顶会话
 * @param sessionId 会话ID
 * @param pinned true-置顶，false-取消置顶
 */
export function pinSession(sessionId: string, pinned: boolean): Promise<PinSessionRes> {
  return request.put(`/ai-api/v1/agent/session/pin?sessionId=${sessionId}&pinned=${pinned}`)
}

/**
 * 收藏/取消收藏会话
 * @param sessionId 会话ID
 * @param favorited true-收藏，false-取消收藏
 */
export function favoriteSession(sessionId: string, favorited: boolean): Promise<FavoriteSessionRes> {
  return request.put(`/ai-api/v1/agent/session/favorite?sessionId=${sessionId}&favorited=${favorited}`)
}

/**
 * 举报会话
 * @param params.sessionId 会话ID
 * @param params.reportType 举报类型：1-违法违规，2-色情低俗，3-虚假信息，4-侵权内容，5-其他
 * @param params.reason 举报补充说明（可选）
 */
export function reportSession(params: ReportSessionParams): Promise<ReportSessionRes> {
  return request.post(`/ai-api/v1/agent/session/report`, params)
}
